import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { QrCode, Printer, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog d'impression d'un QR code "Chantier" pour pointage des heures par les
 * équipes terrain.
 *
 * Le QR encode l'URL `${origin}/fiches-salaries?prefill_order={id}&week={ISO}`.
 * Au scan, le salarié arrive sur "Fiches salariés" qui ouvre automatiquement le
 * dialog de saisie d'heures pré-rempli sur la commande + la semaine choisies.
 *
 * Props : open, onOpenChange, order { id, reference, client_name, city }
 */
export const OrderQRCodeDialog = ({ open, onOpenChange, order }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [weekIso, setWeekIso] = useState(getCurrentWeekIso());
  const [dataUrl, setDataUrl] = useState("");

  const url = order
    ? `${window.location.origin}/fiches-salaries?prefill_order=${order.id}&week=${weekIso}`
    : "";

  useEffect(() => {
    if (!open || !order || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, url, { width: 320, margin: 2 }, (err) => {
      if (err) {
        // eslint-disable-next-line no-console
        console.error("QR generation failed:", err);
      }
    });
    QRCode.toDataURL(url, { width: 600, margin: 2 }).then(setDataUrl).catch(() => {});
  }, [open, order, url]);

  const handlePrint = () => {
    if (!containerRef.current) return;
    const win = window.open("", "_blank", "width=720,height=900");
    if (!win) {
      toast.error("Le navigateur a bloqué l'ouverture de la fenêtre d'impression");
      return;
    }
    win.document.write(`
      <html><head><title>QR Chantier — ${(order?.reference || "")}</title>
      <style>
        body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; padding: 40px; text-align: center; color: #1e293b; }
        h1 { font-size: 28px; margin: 0 0 8px; }
        h2 { font-size: 20px; margin: 0 0 24px; color: #475569; font-weight: 500; }
        img { width: 380px; height: 380px; margin: 12px auto; display: block; }
        .meta { font-size: 14px; color: #64748b; margin-top: 16px; }
        .meta strong { color: #0f172a; }
        .badge { display: inline-block; padding: 4px 12px; border-radius: 9999px; background: #4f46e5; color: white; font-size: 12px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; }
        .footer { margin-top: 32px; font-size: 11px; color: #94a3b8; }
      </style></head>
      <body>
        <span class="badge">Pointage chantier</span>
        <h1>${order?.reference || "—"}</h1>
        <h2>${order?.client_name || ""} ${order?.city ? "— " + order.city : ""}</h2>
        <img src="${dataUrl}" alt="QR Code" />
        <div class="meta">
          Scannez ce QR avec votre téléphone pour saisir vos heures<br/>
          <strong>Semaine : ${weekIso}</strong>
        </div>
        <div class="footer">Affichez cette feuille sur le chantier — durée de validité 7 jours</div>
        <script>setTimeout(() => { window.print(); }, 350);</script>
      </body></html>
    `);
    win.document.close();
  };

  const handleDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `qr-chantier-${order?.reference || "commande"}-${weekIso}.png`;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="order-qrcode-dialog" className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>QR code chantier — {order?.reference}</DialogTitle>
          <DialogDescription>
            Imprimez et affichez sur le chantier. Au scan, vos équipes accèdent directement à
            la saisie d'heures pré-remplie sur cette commande et la semaine choisie.
          </DialogDescription>
        </DialogHeader>
        <div ref={containerRef} className="flex flex-col items-center gap-3 py-2">
          <div className="rounded-lg border bg-white p-3">
            <canvas ref={canvasRef} width={320} height={320} data-testid="qr-canvas" />
          </div>
          <div className="text-xs text-muted-foreground text-center break-all max-w-[380px]">
            {url}
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="qr-week">Semaine ISO</Label>
          <Input
            id="qr-week"
            type="week"
            value={weekIso}
            onChange={(e) => setWeekIso(e.target.value)}
            data-testid="qr-week-input"
          />
          <p className="text-[11px] text-muted-foreground">
            Format YYYY-Www (ex: 2026-W18). Changez la semaine pour ré-imprimer un nouveau QR.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleDownload} data-testid="qr-download-btn">
            <Download className="h-4 w-4 mr-1" />
            Télécharger
          </Button>
          <Button onClick={handlePrint} data-testid="qr-print-btn">
            <Printer className="h-4 w-4 mr-1" />
            Imprimer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Bouton compact à mettre dans la liste des commandes / fiche commande.
 */
export const OrderQRCodeButton = ({ order, size = "sm", variant = "ghost" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`qr-order-${order.id}`}
        title="QR code chantier (pointage des équipes)"
      >
        <QrCode className="h-4 w-4 text-indigo-600" />
      </Button>
      {open && <OrderQRCodeDialog open={open} onOpenChange={setOpen} order={order} />}
    </>
  );
};

/** Retourne la semaine ISO courante au format YYYY-Www attendu par l'input type="week". */
function getCurrentWeekIso(d = new Date()) {
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7; // Mon=0, Sun=6
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }
  const week = 1 + Math.ceil((firstThursday - target) / 604800000);
  return `${d.getFullYear()}-W${String(week).padStart(2, "0")}`;
}

export default OrderQRCodeDialog;
